import React, { useMemo, useState } from "react";
import { Alert, Button, Empty, Popconfirm, Space, Table, Tag, Tooltip, Upload, message } from "antd";
import {
    DeleteOutlined,
    EditOutlined,
    ExclamationCircleOutlined,
    EyeOutlined,
    UploadOutlined,
} from "@ant-design/icons";

import {
    formatReceiptAmount,
    formatReceiptDate,
    formatReceiptRate,
    receiptOptions,
} from "./transactionReceiptUi";
import { useTranslation } from "../../i18n/LanguageProvider";

function renderCompactText(value, maxWidth = 220) {
    const text = value || "-";

    return (
        <div
            title={text}
            style={{
                maxWidth,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            }}
        >
            {text}
        </div>
    );
}

function ReceiptUploadButton({ record, onUpload, t }) {
    const [uploading, setUploading] = useState(false);

    const uploadProps = {
        accept: ".pdf,application/pdf",
        showUploadList: false,
        beforeUpload: async (file) => {
            setUploading(true);
            try {
                await onUpload(record, file);
            } finally {
                setUploading(false);
            }
            return false;
        },
    };

    return (
        <Upload {...uploadProps}>
            <Button size="small" icon={<UploadOutlined />} loading={uploading}>
                {record.hasReceipt ? t("action.replace") : t("receipt.upload")}
            </Button>
        </Upload>
    );
}

/**
 * Гүйлгээний Баримт table.
 *
 * The columns follow the reference file. There is deliberately no uploader column: who attached
 * a PDF is not part of this module.
 *
 * Every control that changes something - upload, replace, edit, delete - is rendered only for
 * the MANAGE roles. A VIEW role (AUTHOR, CUSTOMER) sees the rows and the "харах" button for the
 * attached PDF and nothing else; the backend rejects the write calls regardless.
 */
function TransactionReceiptTable({
    rows,
    loading,
    error,
    pagination,
    onChange,
    canUploadReceipt,
    currencyOptions = [],
    institutionOptions = [],
    activeFilters = {},
    canEdit,
    canDelete,
    rowSelection,
    onViewReceipt,
    onUploadReceipt,
    onEdit,
    onDelete,
    onReload,
}) {
    const { t } = useTranslation();

    const handleUpload = async (record, file) => {
        try {
            await onUploadReceipt(record, file);
        } catch (err) {
            message.error(err?.message || t("receipt.uploadFailed"));
        }
    };

    const columns = useMemo(
        () => [
            {
                title: t("col.id"),
                dataIndex: "id",
                key: "id",
                width: 90,
                fixed: "left",
                sorter: true,
                render: (value) => <span style={{ fontWeight: 600 }}>{value}</span>,
            },
            {
                title: t("col.name"),
                dataIndex: "name",
                key: "name",
                width: 260,
                sorter: true,
                render: (value) => renderCompactText(value, 250),
            },
            {
                title: t("col.currency"),
                dataIndex: "currency",
                key: "currency",
                width: 120,
                sorter: true,
                filters: currencyOptions.map((item) => ({ text: item, value: item })),
                filteredValue: activeFilters.currency || null,
                filterSearch: true,
                render: (value) => (value ? <Tag color="blue">{value}</Tag> : "-"),
            },
            {
                title: t("col.institution"),
                dataIndex: "institution",
                key: "institution",
                width: 140,
                sorter: true,
                filters: institutionOptions.map((item) => ({ text: item, value: item })),
                filteredValue: activeFilters.institution || null,
                filterSearch: true,
                render: (value) => (value ? <Tag>{value}</Tag> : "-"),
            },
            {
                title: t("col.amount"),
                dataIndex: "amount",
                key: "amount",
                width: 160,
                align: "right",
                sorter: true,
                render: (value) => <span style={{ fontWeight: 600 }}>{formatReceiptAmount(value)}</span>,
            },
            {
                title: t("col.executedDate"),
                dataIndex: "executionDate",
                key: "executionDate",
                width: 140,
                sorter: true,
                defaultSortOrder: "descend",
                render: (value) => formatReceiptDate(value),
            },
            {
                title: t("col.rate"),
                dataIndex: "rate",
                key: "rate",
                width: 120,
                align: "right",
                sorter: true,
                render: (value) => formatReceiptRate(value),
            },
            {
                title: t("col.receipt"),
                dataIndex: "hasReceipt",
                key: "hasReceipt",
                width: canUploadReceipt ? 230 : 150,
                fixed: "right",
                filters: receiptOptions(t).map((item) => ({
                    text: item.label,
                    value: item.value,
                })),
                filteredValue: activeFilters.hasReceipt || null,
                filterMultiple: false,
                render: (_, record) => (
                    <Space wrap size={6}>
                        {record.hasReceipt ? (
                            <Button
                                size="small"
                                type="primary"
                                ghost
                                icon={<EyeOutlined />}
                                onClick={() => onViewReceipt(record)}
                            >
                                {t("action.view")}
                            </Button>
                        ) : (
                            <Tag
                                icon={<ExclamationCircleOutlined />}
                                color="error"
                                style={{ fontWeight: 600, margin: 0 }}
                            >
                                {t("receipt.none")}
                            </Tag>
                        )}

                        {canUploadReceipt && (
                            <ReceiptUploadButton record={record} onUpload={handleUpload} t={t} />
                        )}
                    </Space>
                ),
            },
            ...(canEdit || canDelete
                ? [
                      {
                          title: t("col.actions"),
                          key: "actions",
                          width: 110,
                          fixed: "right",
                          render: (_, record) => (
                              <Space wrap size={6}>
                                  {canEdit && (
                                      <Tooltip title={t("action.edit")}>
                                          <Button
                                              size="small"
                                              aria-label={t("action.edit")}
                                              icon={<EditOutlined />}
                                              onClick={() => onEdit(record)}
                                          />
                                      </Tooltip>
                                  )}

                                  {canDelete && (
                                      <Popconfirm
                                          title={t("delete.confirmTitle")}
                                          description={t("receipt.delete.confirmBody")}
                                          okText={t("action.delete")}
                                          cancelText={t("action.cancel")}
                                          okButtonProps={{ danger: true }}
                                          onConfirm={() => onDelete(record)}
                                      >
                                          <Tooltip title={t("action.delete")}>
                                              <Button
                                                  size="small"
                                                  danger
                                                  aria-label={t("action.delete")}
                                                  icon={<DeleteOutlined />}
                                              />
                                          </Tooltip>
                                      </Popconfirm>
                                  )}
                              </Space>
                          ),
                      },
                  ]
                : []),
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            canUploadReceipt,
            canEdit,
            canDelete,
            onViewReceipt,
            onEdit,
            onDelete,
            currencyOptions,
            institutionOptions,
            activeFilters,
            t,
        ]
    );

    if (error) {
        return (
            <Alert
                type="error"
                showIcon
                message={t("receipt.loadError")}
                description={error}
                action={
                    <Button size="small" onClick={onReload}>
                        {t("action.retry")}
                    </Button>
                }
            />
        );
    }

    return (
        <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={rows}
            onChange={onChange}
            rowSelection={rowSelection}
            size="middle"
            // The checkbox column adds its own width, so the horizontal scroll has to grow with it.
            scroll={{ x: (canEdit || canDelete ? 1560 : 1370) + (rowSelection ? 48 : 0) }}
            pagination={pagination}
            locale={{
                emptyText: loading ? (
                    " "
                ) : (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={t("receipt.empty")}
                    />
                ),
            }}
        />
    );
}

export default TransactionReceiptTable;
